"use client";

import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface PanelCopy {
  eyebrow: string;
  title: string;
  lead: string;
  point1: string;
  point2: string;
  point3: string;
  fact1Label: string;
  fact1Value: string;
  fact2Label: string;
  fact2Value: string;
  fact3Label: string;
  fact3Value: string;
  fact4Label: string;
  fact4Value: string;
  imgAlt: string;
}
const copyData: Record<Lang, PanelCopy> = {
  en: {
    eyebrow: "Food Processing Applications",
    title: "Food Processing Filtration Wire Mesh",
    lead: "Engineered from AISI 316L and 304 stainless steel woven mesh to withstand frequent clean-in-place (CIP/SIP) washdown and high-temperature steam sanitation without particle shedding.",
    point1: "Crevice-free hygienic weaves preventing bacterial entrapment and batch cross-contamination.",
    point2: "High tensile and hydraulic shock resistance under pulsed pressure and repeated washdown cycles.",
    point3: "Precision apertures from 5 µm to 500 µm for sugar syrup clarification, dairy straining, and grain screening.",
    fact1Label: "Alloy Grades",
    fact1Value: "AISI 316L / 304",
    fact2Label: "Filtration Rating",
    fact2Value: "5–500 µm",
    fact3Label: "Sanitation Standard",
    fact3Value: "CIP / SIP Ready",
    fact4Label: "Surface Finish",
    fact4Value: "Ultrasonic Cleaned",
    imgAlt: "Sanitary stainless steel wire mesh filter basket and housing inside a food processing line"
  },
  zh: {
    eyebrow: "食品加工应用",
    title: "食品加工过滤丝网",
    lead: "精选 AISI 316L 与 304 不锈钢编织丝网与荷兰密纹滤布，专为食品饮料与乳制品的高频在位清洗 (CIP/SIP) 及高温蒸汽灭菌设计，杜绝介质脱落。",
    point1: "平滑抗滞留卫生级编织结构，杜绝死角物料残留与批次交叉污染。",
    point2: "优异抗拉强度与经纬咬合力，耐受脉冲水压、水锤冲击与高频清洗。",
    point3: "5 µm 至 500 µm 精准孔径控制，适用于糖浆澄清、乳品过滤与谷物干燥筛分。",
    fact1Label: "材质牌号",
    fact1Value: "AISI 316L / 304",
    fact2Label: "截留精度",
    fact2Value: "5–500 µm",
    fact3Label: "卫生清洗规范",
    fact3Value: "兼容 CIP / SIP",
    fact4Label: "表面处理",
    fact4Value: "超声波除油脱脂",
    imgAlt: "食品加工生产线卫生级不锈钢丝网滤篮与过滤器壳体"
  },
  es: {
    eyebrow: "Aplicaciones en procesamiento de alimentos",
    title: "Malla de alambre para filtración en procesamiento de alimentos",
    lead: "Diseñada en acero inoxidable AISI 316L y 304 para resistir lavados frecuentes in situ (CIP/SIP) y esterilización por vapor sin desprendimiento de partículas.",
    point1: "Tejidos higiénicos sin ranuras que previenen retención bacteriana y contaminación cruzada.",
    point2: "Alta resistencia a la tracción y al choque hidráulico bajo presiones pulsadas y lavados continuos.",
    point3: "Aberturas precisas de 5 µm a 500 µm para clarificación de jarabes, lácteos y tamizado de granos.",
    fact1Label: "Grados de aleación",
    fact1Value: "AISI 316L / 304",
    fact2Label: "Grado de filtración",
    fact2Value: "5–500 µm",
    fact3Label: "Norma de saneamiento",
    fact3Value: "Apto CIP / SIP",
    fact4Label: "Acabado superficial",
    fact4Value: "Limpieza ultrasónica",
    imgAlt: "Cesta de filtro de malla de acero inoxidable sanitario en línea de procesamiento de alimentos"
  },
  de: {
    eyebrow: "Lebensmittelverarbeitung",
    title: "Drahtgewebe für die Filtration in der Lebensmittelverarbeitung",
    lead: "Gefertigt aus Edelstahl-Drahtgewebe AISI 316L und 304 für wiederholte CIP/SIP-Reinigungszyklen und Heißdampf-Sterilisation ohne Partikelabrieb.",
    point1: "Spaltfreie hygienische Gewebestrukturen verhindern Bakterienablagerungen und Kreuzkontamination.",
    point2: "Hohe mechanische Festigkeit und Beständigkeit gegen Druckschläge bei intensiven Waschzyklen.",
    point3: "Präzise Porenweiten von 5 µm bis 500 µm für Sirupklärung, Milchfiltration und Getreidesiebung.",
    fact1Label: "Legierungsgüten",
    fact1Value: "AISI 316L / 304",
    fact2Label: "Filterfeinheit",
    fact2Value: "5–500 µm",
    fact3Label: "Sanitärstandard",
    fact3Value: "CIP / SIP bereit",
    fact4Label: "Oberflächenbehandlung",
    fact4Value: "Ultraschallgereinigt",
    imgAlt: "Sanitär-Edelstahldrahtgewebe-Filterkorb in einer Lebensmittelverarbeitungslinie"
  },
  fr: {
    eyebrow: "Applications agroalimentaires",
    title: "Toile métallique de filtration pour l'agroalimentaire",
    lead: "Conçue à partir de toiles tissées en acier inoxydable AISI 316L et 304 pour résister aux cycles fréquents de NEP/SEP sans relargage de particules.",
    point1: "Tissages hygiéniques sans recoins éliminant les zones mortes et les risques de contamination croisée.",
    point2: "Haute résistance à la traction et aux chocs hydrauliques sous pression pulsée répétée.",
    point3: "Ouvertures de précision de 5 µm à 500 µm pour clarification de sirops, produits laitiers et tamisage.",
    fact1Label: "Nuances d'alliages",
    fact1Value: "AISI 316L / 304",
    fact2Label: "Seuil de filtration",
    fact2Value: "5–500 µm",
    fact3Label: "Norme sanitaire",
    fact3Value: "Compatible NEP / SEP",
    fact4Label: "Finition de surface",
    fact4Value: "Nettoyage ultrasons",
    imgAlt: "Panier filtrant en toile inox sanitaire dans une ligne de production agroalimentaire"
  },
  pt: {
    eyebrow: "Aplicações de processamento de alimentos",
    title: "Malha de arame para filtração em processamento de alimentos",
    lead: "Projetada em malha tecida de aço inoxidável AISI 316L e 304 para suportar lavagens frequentes CIP/SIP e sanitização a vapor sem desprendimento de partículas.",
    point1: "Tramas higiênicas sem frestas prevenindo acúmulo bacteriano e contaminação entre lotes.",
    point2: "Alta resistência mecânica e a choques hidráulicos sob ciclos repetidos de lavagem com pressão.",
    point3: "Aberturas precisas de 5 µm a 500 µm para clarificação de xaropes, laticínios e peneiramento de grãos.",
    fact1Label: "Graus de liga",
    fact1Value: "AISI 316L / 304",
    fact2Label: "Grau de filtração",
    fact2Value: "5–500 µm",
    fact3Label: "Padrão sanitário",
    fact3Value: "Compatível CIP / SIP",
    fact4Label: "Acabamento superficial",
    fact4Value: "Limpeza ultrassônica",
    imgAlt: "Cesto de filtro de malha de aço inoxidável sanitário em linha de processamento de alimentos"
  }
};
export default function FoodProcessingPanel() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  const facts: VariantFact[] = [{
    label: c.fact1Label,
    value: c.fact1Value
  }, {
    label: c.fact2Label,
    value: c.fact2Value
  }, {
    label: c.fact3Label,
    value: c.fact3Value
  }, {
    label: c.fact4Label,
    value: c.fact4Value
  }];
  const points = [c.point1, c.point2, c.point3];
  return <VariantPanel id="food-processing" eyebrow={c.eyebrow} title={c.title} lead={c.lead} points={points} facts={facts} image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
    alt: c.imgAlt
  }} tone="background" href="/FoodProcessingFiltrationPage" />;
}